import {MeasuresHelper} from '@selpia/measures/data-access';
import {DateTools} from '@selpia/shared/utils';
import {Dimension, DimensionLevel, Measure} from '@selpia/shared/models';

import {ParametersDto, ReportConfigDto, TileTableDto} from '../dtos';
import {TileTable, AppParameters, ReportConfig, TabularColumn} from '../models';

export class ParametersHelper {
  static getInitialParams(dimensions: Dimension[], parameters: ParametersDto, allMeasures: Measure[]): AppParameters {
    return {
      initialFocusDimensionLevel: this.getFocusLevel(dimensions, parameters),
      focusMeasures: MeasuresHelper.getAllForCodes(parameters.focusMeasures, allMeasures),
      periods: parameters.periods,
      isAIAssistantEnabled: parameters.isAIAssistantEnabled,
      initialPeriod: parameters.initialPeriod,
      initialDate: DateTools.createDate(parameters.initialDate),
      initialFocusMeasure: this.getInitialMeasure(parameters.initialFocusMeasure, allMeasures),
      growthMeasureLabel: parameters.growthMeasureLabel,
      gridTitle: parameters.gridTitle,
      mainTag: parameters.mainTag,
      tileTable: this.enrichTileTable(parameters.tileTable, allMeasures),
      report: this.enrichReportConfig(parameters.report, allMeasures),
      highlightStrategies: parameters.highlights
    };
  }

  private static getInitialMeasure(measureCode: string, allMeasures: Measure[]): Measure | undefined {
    try {
      return MeasuresHelper.getForCode(measureCode, allMeasures);
    } catch (error) {
      console.error(error);
    }
    return undefined;
  }

  private static enrichReportConfig(report: ReportConfigDto, allMeasures: Measure[]): ReportConfig | undefined {
    try {
      const tabularSummary = report.tabularSummary;
      const measures = MeasuresHelper.getAllForCodes(tabularSummary.measures, allMeasures);
      const columns: TabularColumn[] = tabularSummary.columns.map(column => {
        const formatMeasuresCodes = column.measures.map(code =>
          code?.endsWith('_PY')
            ? code.slice(0, -3)
            : code,
        );
        const formatMeasures = MeasuresHelper.getAllForCodesOrNull(formatMeasuresCodes, allMeasures);
        return {
          ...column,
          measures: MeasuresHelper.getAllForCodesOrNull(column.measures, allMeasures),
          measuresCodes: column.measures.map(measureCode => measureCode || null),
          formatMeasures,
        };
      });

      return {
        ...report,
        tabularSummary: {
          ...tabularSummary,
          measures,
          columns,
        },
      };
    } catch (error) {
      console.error(error);
    }

    return undefined;
  }

  private static enrichTileTable(tileTable: TileTableDto, allMeasures: Measure[]): TileTable | undefined {
    try {
      const columns = tileTable.columns.map(column => ({
        ...column,
        measures: MeasuresHelper.getAllForCodes(column.measures, allMeasures),
      }));

      return {
        ...tileTable,
        columns,
      };
    } catch (error) {
      console.error(error);
    }

    return undefined;
  }

  private static getFocusLevel(dimensions: Dimension[], parameters: ParametersDto): DimensionLevel | undefined {
    try {
      return this.getInitialLevelFromParam(dimensions, parameters.initialFocusDimensionLevel);
    } catch (error) {
      console.error(`Using default DimensionLevel as fallback.`, error);
      return this.getDefaultLevel(dimensions);
    }
  }

  private static getDefaultLevel(dimensions: Dimension[]): DimensionLevel | undefined {
    return dimensions.at(0)
      ?.levels
      .at(0);
  }

  private static getInitialLevelFromParam(dimensions: Dimension[], levelName: string): DimensionLevel | undefined {
    if (!levelName) {
      throw 'initialFocusDimensionLevel-name missing from Parameters.';
    }

    const focusLevel = dimensions?.flatMap((dimension) => dimension.levels)
      .find((level) => level.name === levelName);

    if (!focusLevel) {
      throw 'Failed to find DimensionLevel with matching name.';
    }

    return focusLevel;
  }
}
